import { useEffect, useMemo, useState } from 'react';
import { searchVideos, fetchRelatedVideos } from '../services/video.service';
import type { Video } from '../types/video.types';
import type { VideoCardData } from '../types/video-card.types';
import { buildVideoCardData } from '../utils/video-card';

type SuggestedVideoStatus = 'idle' | 'loading' | 'success' | 'error';

export interface UseSuggestedVideosOptions {
    /** Seed video for related-video suggestions. */
    sourceVideo?: Video | null;
    /** Search query for contextual suggestions. */
    query?: string;
    /** Tag filters for contextual suggestions. */
    tags?: string[];
    /** Maximum cards to expose. */
    limit?: number;
    /** Video IDs to omit from suggestions. */
    excludeVideoIds?: string[];
    /** Video URLs to omit from suggestions. */
    excludeVideoUrls?: string[];
    /** Allows broad fallback suggestions when no context is available. */
    allowFallback?: boolean;
}

export interface UseSuggestedVideosResult {
    videos: VideoCardData[];
    status: SuggestedVideoStatus;
    isLoading: boolean;
}

const cleanValues = (values?: string[]) => (
    values?.map((value) => value.trim()).filter((value) => value.length > 0) ?? []
);

const normalizeKey = (value?: string | null) => value?.trim().toLowerCase() ?? '';

const buildSourceQuery = (sourceVideo?: Video | null) => {
    if (!sourceVideo) {
        return '';
    }

    return sourceVideo.EventName
        || sourceVideo.Participants?.[0]?.ParticipantName
        || sourceVideo.FighterA
        || sourceVideo.Tags?.[0]
        || '';
};

/**
 * Fetches contextual suggested videos using existing related/search endpoints.
 */
export const useSuggestedVideos = ({
    sourceVideo = null,
    query = '',
    tags = [],
    limit = 6,
    excludeVideoIds = [],
    excludeVideoUrls = [],
    allowFallback = false
}: UseSuggestedVideosOptions = {}): UseSuggestedVideosResult => {
    const [videos, setVideos] = useState<VideoCardData[]>([]);
    const [status, setStatus] = useState<SuggestedVideoStatus>('idle');
    const tagKey = cleanValues(tags).join('|');
    const excludeIdKey = cleanValues(excludeVideoIds).join('|');
    const excludeUrlKey = cleanValues(excludeVideoUrls).join('|');
    const sourceUrl = sourceVideo?.Url ?? '';
    const sourceQuery = buildSourceQuery(sourceVideo);
    const trimmedQuery = query.trim() || sourceQuery.trim();
    const hasContext = Boolean(sourceUrl || trimmedQuery || tagKey || allowFallback);

    useEffect(() => {
        if (!hasContext) {
            setVideos([]);
            setStatus('idle');
            return;
        }

        let isMounted = true;
        setStatus('loading');
        const requestTags = tagKey ? tagKey.split('|') : [];
        const excludedIds = new Set((excludeIdKey ? excludeIdKey.split('|') : []).map(normalizeKey));
        const excludedUrls = new Set((excludeUrlKey ? excludeUrlKey.split('|') : []).map(normalizeKey));

        const request = sourceUrl
            ? fetchRelatedVideos(sourceUrl, limit)
            : searchVideos(trimmedQuery, 1, limit, requestTags);

        request
            .then((response) => {
                if (!isMounted) {
                    return;
                }

                const nextVideos = (response.Results ?? [])
                    .filter((video) => !excludedIds.has(normalizeKey(video.Id)) && !excludedUrls.has(normalizeKey(video.Url)))
                    .slice(0, limit)
                    .map(buildVideoCardData);

                setVideos(nextVideos);
                setStatus('success');
            })
            .catch(() => {
                if (!isMounted) {
                    return;
                }

                setVideos([]);
                setStatus('error');
            });

        return () => {
            isMounted = false;
        };
    }, [
        allowFallback,
        excludeIdKey,
        excludeUrlKey,
        hasContext,
        limit,
        sourceUrl,
        tagKey,
        trimmedQuery
    ]);

    return useMemo(() => ({
        videos,
        status,
        isLoading: status === 'loading'
    }), [status, videos]);
};
